import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Input,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import ReactSelect, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { buildGroupedOptions } from "../../../utils/buildGroupedOptions";
import { selectStyles } from "../../../utils/selectStyel";

const operatorOptions = [
  { value: "==", label: "is equal to" },
  { value: "!=", label: "is not equal to" },
  { value: ">", label: "is greater than" },
  { value: "<", label: "is less than" },
  { value: ">=", label: "is greater or equal" },
  { value: "<=", label: "is less or equal" },
  { value: "in", label: "is in" },
  { value: "contains", label: "contains" },
];

export const ConditionRules = ({
  control,
  register,
  path,
  groupData,
  fieldKey,
  sectionKey,
  mainSchemaData,
}) => {
  const group = groupData || { Operator: "all", Order: "high", Value: [] };
  if (!group.Field && fieldKey) {
    group.Field = `${fieldKey}_CONDITION`;
  }
  if (!group.Operator) group.Operator = "all";
  if (!group.Order) group.Order = "high";
  if (!group.Value) group.Value = group.Order === "high" ? [] : "";

  const isHighOrder = group.Order === "high";

  // Only use useFieldArray for high order
  const { fields, append, remove } = useFieldArray({
    control,
    name: isHighOrder ? `${path}.Value` : undefined,
  });

  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && isHighOrder && fields.length === 0) {
      if (!groupData) {
        append({ Field: "", Operator: "==", Value: "", Order: "base" });
      } else if (Array.isArray(group.Value) && group.Value.length > 0) {
        group.Value.forEach((item) => append(item));
      } else if (group.Order === "high") {
        append({ Field: "", Operator: "==", Value: "", Order: "base" });
      }
      initialized.current = true;
    }
  }, [groupData, group.Order, append, fields.length, group.Value, isHighOrder]);

  // Form paths
  const fieldField = `${path}.Field`;
  const operatorField = `${path}.Operator`;
  const orderField = `${path}.Order`;

  const groupedOptions = buildGroupedOptions(mainSchemaData.CONFIG);

  return (
    <VStack
      spacing={3}
      align="stretch"
      border="1px solid #CBD5E0"
      p={3}
      borderRadius="md"
      bg="gray.50"
      ml={path.split(".").length > 1 ? 6 : 0}
    >
      <input type="hidden" {...register(fieldField)} value={group.Field} />
      <input type="hidden" {...register(orderField)} value={group.Order} />

      {isHighOrder && (
        <Box w="32%">
          <Controller
            control={control}
            name={operatorField}
            defaultValue={group.Operator || "all"}
            render={({ field: { onChange, value, ref } }) => (
              <ReactSelect
                inputRef={ref}
                value={[
                  { value: "all", label: "All" },
                  { value: "any", label: "Any" },
                ].find((option) => option.value === value)}
                onChange={(selectedOption) => onChange(selectedOption?.value)}
                options={[
                  { value: "all", label: "All" },
                  { value: "any", label: "Any" },
                ]}
                placeholder="Select operator"
                isClearable={false}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "32px",
                    fontSize: "14px",
                  }),
                }}
              />
            )}
          />
        </Box>
      )}

      {isHighOrder &&
        fields.map((item, index) => {
          const fieldPath = `${path}.Value.${index}`;

          if (item.Order === "high" || Array.isArray(item.Value)) {
            return (
              <Box key={index} position="relative">
                <ConditionRules
                  control={control}
                  register={register}
                  path={fieldPath}
                  groupData={item}
                  fieldKey={fieldKey}
                  mainSchemaData={mainSchemaData}
                  sectionKey={sectionKey}
                />
                <IconButton
                  icon={<DeleteIcon />}
                  onClick={() => remove(index)}
                  size="xs"
                  colorScheme="red"
                  aria-label="Remove Group"
                  position="absolute"
                  right={0}
                  top={0}
                />
              </Box>
            );
          }

          // Base condition inside high order group
          return (
            <Flex key={index} align="center" gap={3}>
              <input
                type="hidden"
                {...register(`${fieldPath}.Order`)}
                value="base"
              />

              <Box width="100%">
                <Controller
                  control={control}
                  name={`${fieldPath}.Field`}
                  defaultValue={item.Field || ""}
                  render={({ field }) => {
                    const flatOptions = groupedOptions.flatMap(
                      (group) => group.options || []
                    );

                    return (
                      <CreatableSelect
                        components={{
                          Control: ControlWithTooltip,
                        }}
                        options={groupedOptions}
                        placeholder="Select or type new field"
                        value={
                          flatOptions.find(
                            (opt) => opt.value === field.value
                          ) ||
                          (field.value
                            ? { value: field.value, label: field.value }
                            : null)
                        }
                        onChange={(selected) => field.onChange(selected?.value)}
                        onCreateOption={(inputValue) =>
                          field.onChange(inputValue)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                        isClearable
                      />
                    );
                  }}
                />
              </Box>

              <Controller
                control={control}
                name={`${fieldPath}.Operator`}
                defaultValue={item.Operator || "=="}
                render={({ field: { onChange, value, ref } }) => (
                  <ReactSelect
                    inputRef={ref}
                    value={operatorOptions.find(
                      (option) => option.value === value
                    )}
                    onChange={(selectedOption) =>
                      onChange(selectedOption?.value)
                    }
                    options={operatorOptions}
                    placeholder="Select operator"
                    isClearable={false}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
              />
              <Input
                borderRadius="md"
                borderColor="gray.300"
                bg="white"
                {...register(`${fieldPath}.Value`)}
                defaultValue={item.Value}
                placeholder="Value"
              />
              <Controller
                control={control}
                name={`${fieldPath}.ValueType`}
                defaultValue={group.ValueType || ""}
                render={({ field }) => (
                  <Checkbox
                    isChecked={field.value === "Reference"}
                    onChange={(e) =>
                      field.onChange(e.target.checked ? "Reference" : "")
                    }
                  >
                    Is Reference?
                  </Checkbox>
                )}
              />
              <IconButton
                icon={<DeleteIcon />}
                onClick={() => remove(index)}
                size="xs"
                colorScheme="red"
                aria-label="Remove Condition"
              />
            </Flex>
          );
        })}

      {/* Add new condition / group buttons */}
      {isHighOrder && (
        <Flex gap={2} mt={2}>
          <Button
            size="sm"
            leftIcon={<AddIcon />}
            onClick={() =>
              append({ Field: "", Operator: "==", Value: "", Order: "base" })
            }
          >
            Add Condition
          </Button>

          <Button
            size="sm"
            leftIcon={<AddIcon />}
            onClick={() =>
              append({
                Field: "",
                Operator: "",
                Value: [],
                Order: "high",
                ValueType: "",
              })
            }
          >
            Add Group
          </Button>
        </Flex>
      )}

      {group.Order === "base" && (
        <Flex align="center" gap={3}>
          <Box w="100%">
            <Controller
              control={control}
              name={`${path}.Field`}
              defaultValue={group.Field || ""}
              render={({ field }) => {
                const flatOptions = groupedOptions.flatMap(
                  (g) => g.options || []
                );

                const selectedOption =
                  flatOptions.find((opt) => opt.value === field.value) ||
                  (field.value
                    ? { value: field.value, label: field.value }
                    : null);

                return (
                  <CreatableSelect
                    components={{
                      Control: ControlWithTooltip,
                    }}
                    options={groupedOptions}
                    placeholder="Select or type new field"
                    value={selectedOption}
                    onChange={(selected) => field.onChange(selected?.value)}
                    onCreateOption={(inputValue) => field.onChange(inputValue)}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                    isClearable
                    formatGroupLabel={(group) => (
                      <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>
                        {group.label}
                      </div>
                    )}
                  />
                );
              }}
            />
          </Box>

          <Box w="100%">
            <Controller
              control={control}
              name={`${path}.Operator`}
              defaultValue={group.Operator || "=="}
              render={({ field: { onChange, value, ref } }) => (
                <ReactSelect
                  inputRef={ref}
                  value={operatorOptions.find(
                    (option) => option.value === value
                  )}
                  onChange={(selectedOption) => onChange(selectedOption?.value)}
                  options={operatorOptions}
                  placeholder="Select operator"
                  isClearable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              )}
            />
          </Box>

          <Input
            borderRadius="md"
            borderColor="gray.300"
            bg="white"
            {...register(`${path}.Value`)}
            defaultValue={group.Value || ""}
            placeholder="Value"
          />
          <Controller
            control={control}
            name={`${path}.ValueType`}
            defaultValue={group.ValueType || ""}
            render={({ field }) => (
              <Checkbox
                isChecked={field.value === "Reference"}
                onChange={(e) =>
                  field.onChange(e.target.checked ? "Reference" : "")
                }
              >
                Is Reference?
              </Checkbox>
            )}
          />
        </Flex>
      )}
    </VStack>
  );
};

const ControlWithTooltip = (props) => {
  const value = props.getValue?.()[0];

  return (
    <Tooltip
      label={value?.label}
      placement="top"
      hasArrow
      openDelay={300}
      isDisabled={!value?.label}
      shouldWrapChildren
    >
      <Box display="flex" width="100%">
        <components.Control {...props} />
      </Box>
    </Tooltip>
  );
};
